import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import CustomToaster from '@components/CustomToaster';
import { AppContextProvider } from '@contexts/AppContext';
import useScrollPosition from '@hooks/useScrollPosition';
import { EventProperties } from '@tracking';
import { NavCurrentUser } from '@utils/backend-api/currentUser';

import NavContent from './NavContent';

const queryClient = new QueryClient();

type NavProps = {
  analytics?: EventProperties;
  currentUser: NavCurrentUser;
  flashMessages?: string[];
  overrideOfferCode?: string;
};

export default function Nav({
  analytics,
  currentUser,
  flashMessages,
  overrideOfferCode,
}: NavProps) {
  const [isScrollStyle, setIsScrollStyle] = useState(currentUser.shouldShowScrolledNav);
  const navRef = useRef(null);

  useScrollPosition({
    effect: ({ currPos }) => {
      if (!currentUser.shouldShowScrolledNav) {
        // show scrolled nav effect if user scrolls past top nav
        const showScrollEffect =
          navRef.current?.offsetHeight &&
          navRef.current?.offsetHeight + navRef.current?.offsetTop < Math.abs(currPos.y);
        setIsScrollStyle(showScrollEffect);
      }
    },
    deps: [navRef.current?.offsetHeight, currentUser.shouldShowScrolledNav],
  });

  return (
    <AppContextProvider initialCurrentUser={currentUser}>
      <QueryClientProvider client={queryClient}>
        <div
          ref={navRef}
          className={clsx('h-56 print:hidden sm:pt-40', {
            'fixed sm:h-64': currentUser.shouldShowScrolledNav,
            'sm:h-[164px] md:h-[175px]': !currentUser.shouldShowScrolledNav,
            'border-0 border-b border-solid border-grayscale-charcoal sm:border-b-0':
              !isScrollStyle,
          })}
        >
          <div
            className={clsx('px-10 py-12 sm:border-none sm:px-28 sm:py-0', {
              'fixed left-0 right-0 top-0 h-auto bg-grayscale-charcoal': isScrollStyle,
              'relative h-full': !isScrollStyle,
            })}
          >
            <NavContent
              analytics={analytics}
              className="mx-auto max-w-container-lg"
              currentUser={currentUser}
              isScrollStyle={isScrollStyle}
              overrideOfferCode={overrideOfferCode}
            />
            <CustomToaster flashMessages={flashMessages} />
          </div>
        </div>
      </QueryClientProvider>
    </AppContextProvider>
  );
}
